import { queryClient } from "@/queryClient.ts";
import * as Sentry from "@sentry/react";
import {
  ACTIVITY_EVENTS,
  ATTEMPTS_KEY,
  CUTOFF_HOUR,
  IDLE_MS,
  LAST_PERIOD_KEY,
  MAX_ATTEMPTS,
  RELOAD_RESET_MS,
  SINGLETON_SYMBOL,
  TICK_MS,
} from "./constants.ts";

export interface DailyRefresherDeps {
  now?: () => number;
  reload?: () => void;
}

interface Attempts {
  period: string;
  count: number;
}

const isAttempts = (value: unknown): value is Attempts =>
  typeof value === "object" &&
  value !== null &&
  "period" in value &&
  "count" in value &&
  typeof value.period === "string" &&
  typeof value.count === "number" &&
  Number.isFinite(value.count);

export class DailyRefresher {
  private readonly now: () => number;
  private readonly reload: () => void;

  private lastActivity: number;
  private isStarted = false;
  private isRefreshing = false;
  private pending = false;

  private tickTimer: ReturnType<typeof setInterval> | null = null;
  private reloadResetTimer: ReturnType<typeof setTimeout> | null = null;

  private constructor(deps: DailyRefresherDeps = {}) {
    this.now = deps.now ?? Date.now;
    this.reload = deps.reload ?? (() => window.location.reload());
    this.lastActivity = this.now();
  }

  static getInstance(deps?: DailyRefresherDeps): DailyRefresher {
    const store = globalThis as Record<symbol, DailyRefresher | undefined>;
    const existing = store[SINGLETON_SYMBOL];
    if (existing) {
      return existing;
    }
    const instance = new DailyRefresher(deps);
    store[SINGLETON_SYMBOL] = instance;
    return instance;
  }

  start(): void {
    if (this.isStarted) {
      return;
    }
    this.isStarted = true;
    this.isRefreshing = false;
    this.pending = false;
    this.lastActivity = this.now();

    if (!this.readLastPeriod()) {
      this.writeLastPeriod(this.currentPeriod(this.now()));
    }

    document.addEventListener("visibilitychange", this.onVisibility);
    window.addEventListener("focus", this.onFocus);
    window.addEventListener("online", this.onOnline);
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, this.onActivity, { passive: true, capture: true });
    }
    this.tickTimer = setInterval(this.onTick, TICK_MS);
  }

  stop(): void {
    if (!this.isStarted) {
      return;
    }
    this.isStarted = false;

    document.removeEventListener("visibilitychange", this.onVisibility);
    window.removeEventListener("focus", this.onFocus);
    window.removeEventListener("online", this.onOnline);
    for (const event of ACTIVITY_EVENTS) {
      window.removeEventListener(event, this.onActivity, { capture: true });
    }
    if (this.tickTimer !== null) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
    if (this.reloadResetTimer !== null) {
      clearTimeout(this.reloadResetTimer);
      this.reloadResetTimer = null;
    }
  }

  private readonly onVisibility = (): void => {
    if (document.visibilityState === "visible") {
      this.maybeRefresh();
    }
  };

  private readonly onFocus = (): void => {
    this.maybeRefresh();
  };

  private readonly onOnline = (): void => {
    if (this.pending) {
      this.maybeRefresh();
    }
  };

  private readonly onActivity = (): void => {
    this.lastActivity = this.now();
  };

  private readonly onTick = (): void => {
    if (this.pending) {
      this.maybeRefresh();
      return;
    }
    if (document.visibilityState === "visible" && this.now() - this.lastActivity >= IDLE_MS) {
      this.maybeRefresh();
    }
  };

  private maybeRefresh(): void {
    try {
      if (this.isRefreshing) {
        return;
      }

      const period = this.currentPeriod(this.now());
      const lastPeriod = this.readLastPeriod();
      if (!lastPeriod) {
        this.writeLastPeriod(period);
        return;
      }
      if (period <= lastPeriod) {
        return;
      }

      if (this.isBlocked()) {
        this.pending = true;
        return;
      }

      const attempts = this.readAttempts();
      if (attempts.period === period && attempts.count >= MAX_ATTEMPTS) {
        Sentry.captureMessage(`[DailyRefresher] reached MAX_ATTEMPTS (${MAX_ATTEMPTS})`, {
          level: "warning",
          tags: { module: "daily-refresher" },
          extra: { period, lastPeriod, attempts: attempts.count },
        });
        return;
      }

      this.pending = false;
      this.writeLastPeriod(period);
      this.writeAttempts({
        period,
        count: attempts.period === period ? attempts.count + 1 : 1,
      });
      this.isRefreshing = true;
      this.reloadResetTimer = setTimeout(() => {
        this.isRefreshing = false;
        this.reloadResetTimer = null;
      }, RELOAD_RESET_MS);
      this.reload();
    } catch (error) {
      Sentry.captureException(error, { tags: { module: "daily-refresher" } });
    }
  }

  private isBlocked(): boolean {
    return queryClient.isMutating() > 0 || navigator.onLine === false;
  }

  private currentPeriod(now: number): string {
    const date = new Date(now);
    if (date.getHours() < CUTOFF_HOUR) {
      date.setDate(date.getDate() - 1);
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  private readItem(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writeItem(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch {}
  }

  private readLastPeriod(): string | null {
    return this.readItem(LAST_PERIOD_KEY);
  }

  private writeLastPeriod(period: string): void {
    this.writeItem(LAST_PERIOD_KEY, period);
  }

  private readAttempts(): Attempts {
    const empty: Attempts = { period: "", count: 0 };
    const raw = this.readItem(ATTEMPTS_KEY);
    if (!raw) {
      return empty;
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      if (isAttempts(parsed)) {
        return parsed;
      }
    } catch {
      return empty;
    }
    return empty;
  }

  private writeAttempts(attempts: Attempts): void {
    this.writeItem(ATTEMPTS_KEY, JSON.stringify(attempts));
  }
}
